import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * FlashPill — short, prominent warning rendered everywhere a lead/job appears.
 *
 * Renders `lead.propertyDetails.flashNote` (≤120 chars, operator-typed).
 * Stays visible on completed jobs — property-level facts (dog on site, gate
 * code, etc.) don't expire when a single visit closes.
 *
 * Returns `null` when no flash is set, so it's safe to drop into any header.
 *
 * Sizes:
 *   - sm  → compact, for list/card surfaces (Pipeline, Today mobile card)
 *   - md  → header, for profile pages, work order, job detail
 */
export default function FlashPill({
  propertyDetails,
  size = 'md',
  className = '',
  testId = 'flash-pill',
}) {
  const flash = (propertyDetails?.flashNote || '').trim();
  if (!flash) return null;

  const isSm = size === 'sm';
  const wrap = isSm
    ? 'gap-1 px-2 py-0.5 text-[11px]'
    : 'gap-1.5 px-2.5 py-1 text-xs';
  const icon = isSm ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <span
      className={`inline-flex items-center rounded-md border border-amber-300 bg-amber-50 text-amber-900 font-medium leading-tight max-w-full ${wrap} ${className}`}
      data-testid={testId}
      title={flash}
    >
      <AlertTriangle className={`${icon} shrink-0 text-amber-600`} />
      <span className="truncate">{flash}</span>
    </span>
  );
}
